'use client'

import { Search, SlidersHorizontal } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'

import { IGiftCategory, IGiftDeal, IGiftProduct, IGlobalDiscount } from '../types/schema'
import ProductCard from './ProductCard'
import { createHash } from 'crypto'

interface ProductListProps {
  categories: IGiftCategory[]
  promotions: IGiftDeal[]
  selectedCategory: string | null
  onCategoryChange?: (category: string | null) => void
  locale: string
  globalDiscount?: IGlobalDiscount
}

type SortOption = 'default' | 'price-asc' | 'price-desc'

export default function ProductList({
  categories,
  promotions,
  selectedCategory,
  onCategoryChange,
  locale,
  globalDiscount
}: ProductListProps) {
  const t = useTranslations()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('default')

  // Handle buy now - redirect to PHP cart
  const handleBuyNow = (product: IGiftProduct) => {
    const phpBaseUrl = process.env.NEXT_PUBLIC_API_GATEWAY || 'https://dev.oasisspa.net'
    if (window.dataLayer) {
      const hash = createHash('sha256')
      hash.update(product.id.toString())
      const hashedId = hash.digest('hex')
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          type: 'eGift',
          currency: 'THB',
          items: [{
            item_id: hashedId,
            item_name: product.name,
            price: product.price,
            quantity: 1,
            category: product.category,
          }]
        }
      })
    }
    const cartUrl = `${phpBaseUrl}/${locale}/onlinegift/cart/?gift_id=${product.id}`
    window.location.href = cartUrl
  }

  // Find promotion info for a product
  const getProductPromotion = (productId: string) => {
    return promotions.find((promo) => promo.id === productId)
  }

  // Flatten all products for easier filtering - include individual promotion products
  const allProducts = useMemo(() => {
    const categoryProducts = categories.flatMap((cat) =>
      cat.product.map((prod) => ({ ...prod, categoryName: cat.category }))
    )

    const promotionProducts = promotions
      .filter((promo) => !categoryProducts.some((cat) => cat.id === promo.id))
      .map((promo) => ({
        id: promo.id,
        name: promo.name,
        price: promo.originalPrice,
        category: promo.category,
        categoryName: promo.category,
        hour: promo.duration || 0,
        expiry: promo.expiry || 365,
        detail: promo.detail || '',
        img: promo.hasImage ? 1 : 0,
        imgURI: promo.imgURI,
        disc: undefined,
        disc_t: undefined,
        promo_end: promo.promoEndDate || undefined
      }))

    return [...categoryProducts, ...promotionProducts]
  }, [categories, promotions])

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = allProducts

    // Filter by Category
    if (selectedCategory) {
      result = result.filter((p) => p.categoryName === selectedCategory)
    }

    // Filter by Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter((p) => p.name.toLowerCase().includes(query))
    }

    // Sort
    return result.sort((a, b) => {
      const priceA = getProductPromotion(a.id)?.salePrice ?? a.price
      const priceB = getProductPromotion(b.id)?.salePrice ?? b.price

      switch (sortBy) {
        case 'price-asc':
          return priceA - priceB
        case 'price-desc':
          return priceB - priceA
        default:
          return a.name.localeCompare(b.name)
      }
    })
  }, [allProducts, selectedCategory, searchQuery, sortBy, promotions])

  // Search Fallback Logic
  useEffect(() => {
    if (searchQuery && selectedCategory && filteredProducts.length === 0) {
      const hasMatchesInAll = allProducts.some((p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      )

      if (hasMatchesInAll) {
        onCategoryChange?.(null)
      }
    }
  }, [searchQuery, selectedCategory, filteredProducts, allProducts, onCategoryChange])

  return (
    <section className='py-8 md:py-12'>
      <div className='mb-8'>
        <h2
          className='mb-2 text-center text-2xl font-bold'
          dangerouslySetInnerHTML={{
            __html: t.raw('eGift : Home Page.GIFTCERTIFICATE_buy_now_cry') as string
          }}
        />
        <div
          className='text-gray-600 [&_a:hover]:text-blue-800 [&_a]:text-blue-600 [&_a]:underline'
          dangerouslySetInnerHTML={{
            __html: t.raw('eGift : Home Page.GIFTCERTIFICATE_buy_now_desc') as string
          }}
        />
      </div>
      <div className='mb-8 flex flex-col items-center justify-between gap-6 text-center md:flex-row md:items-center md:text-left'>
        <div>
          <h2 className='text-3xl font-bold text-gray-900'>
            {t('eGift : Home Page.PRODUCT_LIST').toUpperCase()}
          </h2>
        </div>

        {/* Search and Sort Controls */}
        <div className='flex w-full flex-col gap-4 md:w-auto md:flex-row'>
          <div className='relative w-full md:w-auto'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
            <input
              type='text'
              placeholder={t('eGift : Home Page.search_packages')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='w-full rounded-full border border-gray-200 bg-gray-50 py-2 pl-10 pr-4 text-sm outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 md:w-64'
            />
          </div>

          <div className='relative w-full md:w-auto'>
            <SlidersHorizontal className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className='w-full appearance-none rounded-full border border-gray-200 bg-gray-50 py-2 pl-10 pr-8 text-sm outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 md:w-auto'>
              <option value='default'>{t('eGift : Home Page.filter_by')}</option>
              <option value='price-asc'>{t('eGift : Home Page.price_low_high')}</option>
              <option value='price-desc'>{t('eGift : Home Page.price_high_low')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Category Pills */}
      <div className='mb-8 max-w-full overflow-x-auto pb-2 md:mb-10'>
        <div className='flex min-w-max gap-3 px-4 md:min-w-0 md:gap-2 md:px-0'>
          <button
            onClick={() => onCategoryChange?.(null)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all md:px-6 ${!selectedCategory
              ? 'bg-gray-900 text-white shadow-lg'
              : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}>
            {t('eGift : Home Page.all_categories')}
          </button>
          {categories.map((category) => (
            <button
              key={category.category}
              onClick={() => onCategoryChange?.(category.category)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all md:px-6 ${selectedCategory === category.category
                ? 'bg-gray-900 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}>
              {category.category}
            </button>
          ))}
        </div>
      </div>

      {/* Product Grid */}
      {filteredProducts.length > 0 ? (
        <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              promotion={getProductPromotion(product.id)}
              onBuyNow={handleBuyNow}
              locale={locale}
              globalDiscount={globalDiscount}
            />
          ))}
        </div>
      ) : (
        <div className='flex min-h-[300px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-12 text-center'>
          <div className='mb-4 rounded-full bg-gray-100 p-4'>
            <Search className='h-8 w-8 text-gray-400' />
          </div>
          <h3 className='text-lg font-medium text-gray-900'>
            {t('eGift : Home Page.no_packages_found')}
          </h3>
          <p className='text-gray-500'>{t('eGift : Home Page.try_adjusting')}</p>
          <button
            onClick={() => {
              setSearchQuery('')
              onCategoryChange?.(null)
            }}
            className='mt-4 text-sm font-medium text-amber-600 hover:text-amber-700'>
            {t('eGift : Home Page.clear_filters')}
          </button>
        </div>
      )}
    </section>
  )
}
